:root {
  font-family:
    Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  color: #17202a;
  background: #f6f8fb;
  font-synthesis: none;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

* {
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
}

body {
  margin: 0;
  min-width: 320px;
  min-height: 100vh;
}

button,
a {
  font: inherit;
}

button {
  cursor: pointer;
}

a {
  color: inherit;
  text-decoration: none;
}

.site-shell {
  --bg: #f6f8fb;
  --surface: #ffffff;
  --surface-strong: #f0f4f8;
  --ink: #17202a;
  --muted: #5c6876;
  --line: #dbe3ec;
  --accent: #0f7c6d;
  --accent-2: #355bd6;
  --accent-3: #b84c26;
  --accent-soft: #dff3ef;
  min-height: 100vh;
  color: var(--ink);
  background: var(--bg);
}

.site-shell.theme-dark {
  --bg: #101419;
  --surface: #171d24;
  --surface-strong: #202935;
  --ink: #f3f7fb;
  --muted: #b8c2cf;
  --line: #303c49;
  --accent: #44c0aa;
  --accent-2: #8aa4ff;
  --accent-3: #f09d62;
  --accent-soft: #173b36;
}

@media (prefers-color-scheme: dark) {
  .site-shell.theme-auto {
    --bg: #101419;
    --surface: #171d24;
    --surface-strong: #202935;
    --ink: #f3f7fb;
    --muted: #b8c2cf;
    --line: #303c49;
    --accent: #44c0aa;
    --accent-2: #8aa4ff;
    --accent-3: #f09d62;
    --accent-soft: #173b36;
  }
}

.site-header {
  position: sticky;
  top: 0;
  z-index: 10;
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 24px;
  align-items: center;
  width: min(1180px, calc(100% - 32px));
  margin: 0 auto;
  padding: 18px 0;
  background: color-mix(in srgb, var(--bg) 88%, transparent);
  backdrop-filter: blur(18px);
}

.brand {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-weight: 800;
}

.brand-mark {
  display: grid;
  width: 36px;
  height: 36px;
  place-items: center;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--ink);
  color: var(--bg);
  font-size: 0.82rem;
}

.main-nav {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
}

.main-nav a,
.theme-switcher button,
.install-tabs button,
.provider-grid button,
.mode-list button {
  min-height: 38px;
  border: 1px solid transparent;
  border-radius: 8px;
  color: var(--muted);
  background: transparent;
}

.main-nav a {
  display: inline-flex;
  align-items: center;
  padding: 0 12px;
}

.main-nav a:hover,
.main-nav a:focus-visible {
  color: var(--ink);
  background: var(--surface-strong);
}

.theme-switcher {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: var(--surface);
}

.theme-switcher button {
  min-width: 54px;
  padding: 0 10px;
  font-size: 0.86rem;
}

.theme-switcher button.is-active,
.install-tabs button.is-active,
.provider-grid button.is-active,
.mode-list button.is-active {
  border-color: var(--ink);
  color: var(--ink);
  background: var(--accent-soft);
}

main {
  overflow: hidden;
}

.hero,
.section {
  width: min(1180px, calc(100% - 32px));
  margin: 0 auto;
}

.hero {
  display: grid;
  gap: 28px;
  padding: 72px 0 56px;
}

.hero-copy {
  max-width: 980px;
}

.eyebrow {
  margin: 0 0 12px;
  color: var(--accent);
  font-size: 0.88rem;
  font-weight: 800;
  text-transform: uppercase;
}

h1,
h2,
h3,
p {
  margin-top: 0;
}

h1 {
  max-width: 980px;
  margin-bottom: 18px;
  font-size: clamp(3.2rem, 8vw, 7.8rem);
  line-height: 0.92;
  letter-spacing: 0;
}

h2 {
  margin-bottom: 16px;
  font-size: clamp(2rem, 4vw, 4.2rem);
  line-height: 1;
  letter-spacing: 0;
}

h3 {
  margin-bottom: 10px;
  font-size: 1.08rem;
}

p {
  color: var(--muted);
  line-height: 1.7;
}

.hero-callout {
  max-width: 820px;
  margin-bottom: 16px;
  color: var(--text);
  font-size: clamp(1.34rem, 2.2vw, 2rem);
  font-weight: 750;
  line-height: 1.24;
}

.hero-lede {
  max-width: 780px;
  font-size: 1.18rem;
}

.hero-actions,
.involved-links {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.hero-actions a,
.involved-links a,
.site-footer a {
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  padding: 0 14px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface);
  color: var(--ink);
  font-weight: 700;
}

.hero-actions a:hover,
.involved-links a:hover,
.site-footer a:hover {
  border-color: var(--accent);
  color: var(--accent);
}

.install-panel {
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface);
  box-shadow: 0 24px 80px rgba(31, 45, 61, 0.12);
}

.install-head {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  border-bottom: 1px solid var(--line);
  color: var(--muted);
  font-size: 0.94rem;
}

.install-head span:first-child {
  color: var(--ink);
  font-weight: 800;
}

.install-tabs {
  display: flex;
  gap: 8px;
  padding: 12px;
  border-bottom: 1px solid var(--line);
}

.install-tabs button {
  min-width: 76px;
  padding: 0 14px;
}

pre {
  margin: 0;
  overflow-x: auto;
  white-space: pre;
}

code,
kbd {
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace;
}

.install-panel pre,
.console-card pre,
.mode-preview pre {
  padding: 22px;
  background: #111923;
  color: #d6f6ec;
  font-size: 0.96rem;
}

.section {
  padding: 74px 0;
  border-top: 1px solid var(--line);
}

.intro-grid,
.steer,
.get-involved {
  display: grid;
  grid-template-columns: minmax(240px, 0.8fr) minmax(0, 1.2fr);
  gap: 42px;
  align-items: start;
}

.intro-copy {
  display: grid;
  gap: 12px;
}

.section-heading {
  max-width: 820px;
  margin-bottom: 28px;
}

.feature-grid,
.not-built-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.feature-grid article,
.skill-cta,
.skill-card,
.history-cards article,
.key-grid article,
.not-built-grid article {
  min-height: 100%;
  padding: 20px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface);
}

.feature-grid article:nth-child(2n),
.skill-card:nth-child(2n),
.not-built-grid article:nth-child(2n) {
  background: var(--surface-strong);
}

.review-pull {
  margin: 0 0 16px;
  padding: 24px;
  border: 1px solid var(--accent);
  border-radius: 8px;
  background: var(--accent-soft);
}

.review-pull-secondary {
  border-color: var(--line-strong);
  background: var(--surface-strong);
}

.review-pull blockquote {
  margin: 0;
  max-width: 900px;
  color: var(--ink);
  font-size: clamp(1.25rem, 3vw, 2.2rem);
  font-weight: 800;
  line-height: 1.16;
}

.review-pull figcaption {
  margin-top: 16px;
  color: var(--muted);
  font-weight: 800;
}

.review-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.review-grid article {
  min-height: 100%;
  padding: 20px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface);
}

.review-grid article:nth-child(2n) {
  background: var(--surface-strong);
}

.case-architecture {
  display: grid;
  gap: 14px;
  margin-bottom: 18px;
}

.case-user,
.case-spine {
  padding: 16px 18px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface);
  color: var(--ink);
  font-weight: 900;
}

.case-spine {
  border-color: var(--accent);
  background: var(--accent-soft);
}

.case-branch-grid,
.case-flow-grid,
.case-summary-grid {
  display: grid;
  gap: 14px;
}

.case-branch-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.case-flow-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: 18px;
}

.case-branch-grid article,
.case-flow-grid article,
.case-summary-grid article {
  min-height: 100%;
  padding: 20px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface);
}

.case-branch-grid article:nth-child(2n),
.case-flow-grid article:nth-child(2n),
.case-summary-grid article:nth-child(2n) {
  background: var(--surface-strong);
}

.case-branch-grid span,
.case-flow-grid span,
.case-summary-grid span {
  display: block;
  color: var(--muted);
  line-height: 1.55;
}

.case-branch-grid article > span,
.case-flow-grid article > span {
  margin-bottom: 10px;
  color: var(--accent);
  font-size: 0.78rem;
  font-weight: 900;
  text-transform: uppercase;
}

.case-code-layout {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(320px, 1.1fr);
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface);
}

.case-code-layout > div {
  padding: 24px;
}

.case-code-layout pre {
  padding: 22px;
  background: #111923;
  color: #d6f6ec;
  font-size: 0.88rem;
  line-height: 1.55;
}

.case-summary-grid {
  grid-template-columns: 1fr;
  margin-top: 18px;
}

.case-summary-grid strong {
  display: block;
  margin-bottom: 6px;
}

.command-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  margin-top: 18px;
  padding: 16px 18px;
  border-left: 4px solid var(--accent-3);
  background: var(--surface-strong);
  color: var(--muted);
}

.command-strip code,
.package-command code,
.skill-cta code,
.skill-card code,
.history-cards code {
  display: inline-flex;
  width: fit-content;
  max-width: 100%;
  padding: 6px 9px;
  border-radius: 6px;
  background: var(--ink);
  color: var(--bg);
  overflow-wrap: anywhere;
}

.skills-section .section-heading p {
  max-width: 720px;
}

.skill-cta {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 18px;
  align-items: center;
  margin-bottom: 18px;
  border-color: var(--accent);
  background: var(--accent-soft);
}

.skill-cta span,
.skill-card span {
  display: block;
  margin-bottom: 10px;
  color: var(--accent);
  font-size: 0.78rem;
  font-weight: 900;
  text-transform: uppercase;
}

.pipeline-rail {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 8px;
  margin: 0 0 14px;
  padding: 0;
  list-style: none;
}

.pipeline-rail li {
  padding: 10px 8px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface);
  color: var(--muted);
  font-size: 0.8rem;
  font-weight: 900;
  text-align: center;
}

.skill-matrix-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.skill-card {
  display: grid;
  gap: 8px;
  align-content: start;
}

.skill-card h3,
.skill-card p {
  margin-bottom: 0;
}

.provider-cloud {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 22px;
}

.provider-cloud span {
  padding: 8px 11px;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: var(--surface);
  color: var(--muted);
  font-weight: 700;
}

.model-workbench {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(280px, 0.8fr);
  gap: 18px;
  align-items: stretch;
}

.provider-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.provider-grid button {
  display: grid;
  gap: 8px;
  align-content: start;
  min-height: 118px;
  padding: 16px;
  text-align: left;
  background: var(--surface);
  border-color: var(--line);
}

.provider-grid button span {
  color: var(--muted);
  line-height: 1.45;
}

.console-card {
  display: grid;
  align-content: start;
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface);
}

.console-card p {
  margin: 0;
  padding: 16px 18px;
  border-bottom: 1px solid var(--line);
  color: var(--ink);
  font-weight: 800;
}

.history-layout {
  display: grid;
  grid-template-columns: minmax(260px, 0.85fr) minmax(0, 1.15fr);
  gap: 18px;
}

.session-tree {
  display: grid;
  gap: 8px;
  align-content: start;
  padding: 18px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface);
}

.tree-row {
  padding: 12px 14px;
  border: 1px solid var(--line);
  border-radius: 8px;
  color: var(--muted);
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace;
  overflow-wrap: anywhere;
}

.tree-row:nth-child(n + 2) {
  margin-left: 20px;
}

.tree-row:nth-child(n + 3) {
  margin-left: 40px;
}

.tree-row.is-active {
  border-color: var(--accent);
  color: var(--ink);
  background: var(--accent-soft);
}

.history-cards {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.context-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.key-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

kbd {
  display: inline-flex;
  min-height: 42px;
  align-items: center;
  margin-bottom: 14px;
  padding: 0 12px;
  border: 1px solid var(--line);
  border-bottom-width: 3px;
  border-radius: 8px;
  background: var(--surface-strong);
  color: var(--ink);
  font-weight: 800;
}

.mode-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}

.mode-list button {
  min-width: 128px;
  padding: 0 16px;
  border-color: var(--line);
  background: var(--surface);
}

.mode-preview {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 0.9fr);
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface);
}

.mode-preview > div {
  padding: 24px;
}

.primitive-layout {
  display: grid;
  grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  gap: 28px;
}

.package-command {
  display: grid;
  gap: 10px;
  margin-top: 20px;
}

.primitive-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.primitive-list li {
  padding: 14px 16px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface);
  color: var(--ink);
  font-weight: 700;
}

.not-built-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.not-built-grid span {
  display: block;
  margin-bottom: 10px;
  color: var(--accent-3);
  font-size: 1.08rem;
  font-weight: 900;
}

.get-involved {
  align-items: center;
  padding-bottom: 56px;
}

.site-footer {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  width: min(1180px, calc(100% - 32px));
  margin: 0 auto;
  padding: 28px 0 46px;
  border-top: 1px solid var(--line);
  color: var(--muted);
}

.guide-block {
  margin-bottom: 28px;
}

.guide-block > h3 {
  margin-bottom: 14px;
  font-size: 1.05rem;
}

.guide-steps {
  list-style: none;
  margin: 0;
  padding: 0;
}

.guide-steps li {
  min-height: 100%;
  padding: 20px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--surface);
}

.guide-steps li:nth-child(2n) {
  background: var(--surface-strong);
}

.guide-steps li > span {
  display: block;
  margin-bottom: 10px;
  color: var(--accent);
  font-size: 0.78rem;
  font-weight: 900;
  text-transform: uppercase;
}

.guide-steps li h4 {
  margin: 0 0 8px;
  font-size: 1rem;
}

.guide-steps li p {
  margin: 0;
  color: var(--muted);
  line-height: 1.55;
}

.guide-cols {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 0.9fr);
  gap: 22px;
  align-items: start;
}

.guide-cli {
  display: grid;
  gap: 12px;
  margin: 0;
}

.guide-cli dt {
  margin-bottom: 6px;
}

.guide-cli dd {
  margin: 0;
  color: var(--muted);
  line-height: 1.5;
}

.guide-note {
  margin: 0 0 14px;
  color: var(--muted);
  line-height: 1.6;
}

.guide-tree {
  padding: 18px 20px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #111923;
  color: #d6f6ec;
  font-size: 0.85rem;
  line-height: 1.5;
  overflow-x: auto;
}

@media (max-width: 900px) {
  .site-header {
    grid-template-columns: 1fr;
    justify-items: start;
  }

  .main-nav {
    justify-content: flex-start;
  }

  h1 {
    font-size: clamp(3rem, 13vw, 5.6rem);
  }

  .intro-grid,
  .steer,
  .get-involved,
  .model-workbench,
  .history-layout,
  .mode-preview,
  .case-code-layout,
  .guide-cols,
  .primitive-layout {
    grid-template-columns: 1fr;
  }

  .skill-cta,
  .pipeline-rail {
    grid-template-columns: 1fr;
  }

  .feature-grid,
  .context-grid,
  .history-cards,
  .review-grid,
  .case-branch-grid,
  .case-flow-grid,
  .skill-matrix-grid,
  .not-built-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .provider-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 620px) {
  .hero,
  .section,
  .site-header,
  .site-footer {
    width: min(100% - 24px, 1180px);
  }

  .hero {
    padding-top: 46px;
  }

  .install-head,
  .install-tabs,
  .hero-actions,
  .theme-switcher {
    width: 100%;
  }

  .install-head {
    flex-direction: column;
  }

  .install-tabs,
  .theme-switcher,
  .mode-list {
    overflow-x: auto;
  }

  .feature-grid,
  .context-grid,
  .history-cards,
  .key-grid,
  .review-grid,
  .case-branch-grid,
  .case-flow-grid,
  .not-built-grid,
  .primitive-list,
  .skill-matrix-grid,
  .provider-grid {
    grid-template-columns: 1fr;
  }

  .tree-row:nth-child(n + 2),
  .tree-row:nth-child(n + 3) {
    margin-left: 0;
  }
}
